/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React from "react"
import { ThemeProvider as CD2ThemeProvider } from "@cd2/theme"
import { Helmet } from "@cd2/components"

export const siteColors = {
  default: "rgba(0,0,0,0.83)",
  primary: "#0c9298",
  primaryLight: "#0fb4ba",
  secondary: "#f3b81d",
  lightGray: "#f4f6f7",
  formBorderColor: "#DADADA",
  green: "#46b282",
  red: "#d03b39",
  orange: "#f3b81d",
  lightBlue: "#0c9298",
  errorRed: "#d03b39",
  errorRedText: "#fff",
  lightBorder: "#e2e2e2",
  pills: {
    orange: "#ffe083",
    blue: "#e6f7ff",
    green: "#46b282",
    lightBlue: "#d1daf1",
  },
  backgrounds: {
    grey: "#f4f6f7",
    orange: "#ebcc7d",
    blue: "#c4e7ff",
  },
}

export const siteTheme = {
  borderRadius: 8,
  sizes: {
    fieldGap: 16,
  },
}

export function ThemeProvider(props: {
  children: React.ReactNode
  colors?: Record<string, string | number | undefined>
}) {
  const colors = { ...siteColors, ...(props.colors ?? {}) }

  return (
    <CD2ThemeProvider
      theme={{
        fontFamily: "filson-pro, sans-serif",
        fontWeight: 300,
        colors,
        borderRadius: 4,
        forms: {
          border: {
            radius: 4,
            focusColor: colors.primary,
            color: colors.formBorderColor,
          },
          font: { size: 16 },
        },
        text: {
          colors,
          ratios: { "1": 2.25, "2": 1.66, "3": 1.25, "4": 1, p: 1 },
          lineHeight: 1.5,
          blockSpacing: 0.5,
          sizes: { large: 112.5, normal: 87.5, small: 75, verySmall: 60 },
          weights: {
            defaultHeading: 500,
            defaultParagraph: 300,
          },
        },
        breadcrumb: {
          separator: "›",
          cssContainer: `color: #bbb`,
        },
        alert: {
          padding: "16px 32px",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        },
        button: {
          defaultButtonColor: colors.primary,
          padding: {
            topAndBottom: 12,
            sides: 16,
            innerSides: 8,
          },
          fontSize: 16,
          weight: 500,
        },
        table: {
          cells: {
            padding: 12,
            fontSize: 14,
          },
          headings: {
            weight: 600,
            padding: "6px 12px",
            fontSize: 12,
            color: "#444",
          },
        },
      }}
      globalStyles={css`
        a {
          color: ${colors.primary};
        }

        ul {
          margin: 0;
          padding: 0 0 0 18px;
          color: rgba(0, 0, 0, 0.83);
          line-height: 1.5;
        }
      `}
    >
      <Helmet>
        <link rel="stylesheet" href="https://use.typekit.net/qgv0zcr.css" />
        <link href="https://fonts.googleapis.com/css?family=Robot:400" rel="stylesheet" />
        <style>
          {`body {
            font-weight: 300;
          }`}
        </style>
      </Helmet>
      {props.children}
    </CD2ThemeProvider>
  )
}
